import React, { useState, useEffect } from "react";
import { withRouter } from "react-router-dom";
import { Table, Image, Tag } from "antd";
import { showConfirmDlgWidget } from "components/ConfirmDlgWidget/showConfirmDlgWidget";
import dragula from "dragula";
import "dragula/dist/dragula.css";
import { apiUpdateShopProductPosition } from "services/shopProductService";
import { DragOutlined } from "@ant-design/icons";

const getIndexInParent = (el) => Array.from(el.parentNode.children).indexOf(el);

// Image filenames may contain spaces / commas / other special characters. A raw
// <img src> with those won't load, even though the URL opens fine when pasted into
// the address bar (the browser encodes it). Encode it here (idempotently) so the
// thumbnail loads. decodeURI first avoids double-encoding an already-encoded URL.
const safeImageUrl = (url) => {
  if (!url || typeof url !== "string") return url;
  try {
    return encodeURI(decodeURI(url));
  } catch (e) {
    return encodeURI(url);
  }
};

const stripHtml = (html) => {
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html || "";
  return tmp.textContent || tmp.innerText || "";
};

const parseImages = (images) => {
  if (!images) return [];
  if (Array.isArray(images)) return images;
  if (typeof images === "string") {
    try {
      const parsed = JSON.parse(images);
      if (Array.isArray(parsed)) return parsed;
      if (parsed) return [String(parsed)];
    } catch (e) {
      // Not JSON — a single URL string. Do NOT split on commas: image filenames
      // can legitimately contain commas (e.g. "Sep 10, 2026").
    }
    return [images];
  }
  return [images];
};

function TableWidget(props) {
  const [dataSource, setDataSource] = useState([]);
  const { dataList, onUpdateAction, onDeleteAction } = props;

  useEffect(() => {
    let start;
    let end;
    const container = document.querySelector(".ant-table-tbody");
    const drake = dragula([container], {
      moves: (el) => {
        start = getIndexInParent(el);
        return true;
      },
    });
    drake.on("drop", (el) => {
      end = getIndexInParent(el);
      handleReorder(start, end);
    });
  }, []);

  useEffect(() => {
    if (dataList !== undefined) {
      setDataSource([
        ...dataList.map((info) => {
          return { ...info, key: info.id, icon: <DragOutlined type="swap" /> };
        }),
      ]);
    }
  }, [dataList]);
  if (dataList === undefined) return <div />;

  const columns = [
    {
      title: <DragOutlined type="swap" />,
      dataIndex: "icon",
    },
    {
      title: "ID",
      dataIndex: "id",
    },
    {
      title: "Thumbnail",
      dataIndex: "thumbnail_image",
      render: (text, record) => {
        const images = parseImages(record.thumbnail_image);
        if (images.length === 0) {
          return (
            <img
              className="room-thumbnail-img"
              src={"/assets/icons/ic_picture.svg"}
              style={{ width: "80px", height: "80px", objectFit: "cover", borderRadius: "6px" }}
              alt="product"
            />
          );
        }
        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", maxWidth: "220px" }}>
            {images.map((imgUrl, index) => (
              <Image
                key={index}
                src={safeImageUrl(imgUrl)}
                style={{ width: "80px", height: "80px", objectFit: "cover", borderRadius: "6px", cursor: "pointer" }}
                rootClassName="shop-thumb-clickable"
                preview={{ mask: images.length > 1 ? `${index + 1}/${images.length}` : undefined }}
              />
            ))}
          </div>
        );
      },
    },
    {
      title: "Name",
      dataIndex: "name",
      align: "center",
      sorter: {
        compare: (a, b) => stripHtml(a.name).localeCompare(stripHtml(b.name)),
      },
      render: (text, record) => (
        <div style={{ maxWidth: 220, textAlign: "left" }}>{stripHtml(record.name)}</div>
      ),
    },
    {
      title: "Category",
      dataIndex: "category_name",
      align: "center",
      render: (text, record) =>
        record.category_name ? <Tag color="var(--blueColor)">{record.category_name}</Tag> : <span>-</span>,
    },
    {
      title: "Variants (weight · price · stock)",
      dataIndex: "variants",
      align: "left",
      render: (text, record) => {
        const variants = Array.isArray(record.variants) ? record.variants : [];
        if (variants.length === 0) return <span>-</span>;
        return (
          <div style={{ maxWidth: 260 }}>
            {variants.map((v) => (
              <div key={v.id} style={{ padding: "2px 0" }}>
                <span style={{ fontWeight: 500 }}>{v.weight || v.name || "-"}</span>
                {"  ·  "}
                <span>{v.price}</span>
                {"  ·  "}
                <Tag color={parseInt(v.stock_qty || 0) > 0 ? "green" : "red"}>
                  stock {v.stock_qty || 0}
                </Tag>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      title: "Publish",
      dataIndex: "status",
      align: "center",
      render: (text, record) => {
        return (
          <Tag color={record.status === "1" ? "var(--blueColor)" : "var(--orangeColor)"}>
            {record.status === "1" ? "Yes" : "No"}
          </Tag>
        );
      },
    },
    {
      title: "Updated At",
      dataIndex: "update_timestamp",
      align: "center",
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (text, record) => {
        return (
          <div>
            <a
              style={{ color: "var(--orangeColor)", marginLeft: 20 }}
              className="edit-btn"
              onClick={() => onUpdateAction(record)}
            >
              Edit
            </a>
            <a
              style={{ color: "var(--redColor)", marginLeft: 20 }}
              className="del-btn"
              onClick={async () => {
                var res = await showConfirmDlgWidget({
                  title: "Delete Confirmation",
                  content: "Are you sure to delete this record?",
                });
                if (res == false) return;
                onDeleteAction(record);
              }}
            >
              Delete
            </a>
          </div>
        );
      },
    },
  ];

  const handleReorder = (dragIndex, draggedIndex) => {
    setDataSource((oldState) => {
      const newState = [...oldState];
      const item = newState.splice(dragIndex, 1)[0];
      newState.splice(draggedIndex, 0, item);
      apiUpdateShopProductPosition(newState);
      return newState;
    });
  };

  return (
    <div className="list-table">
      <style>{`
        .shop-thumb-clickable,
        .shop-thumb-clickable .ant-image-img,
        .shop-thumb-clickable .ant-image-mask {
          cursor: pointer !important;
        }
      `}</style>
      <Table pagination={false} columns={columns} dataSource={dataSource} />
    </div>
  );
}

export default withRouter(TableWidget);
